import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { focusElement } from 'platform/utilities/ui';
import * as VAP_SERVICE from '../constants';
import { isPendingTransaction } from '../util/transactions';
import {
  createTransaction,
  refreshTransaction,
  clearTransactionRequest,
  updateFormFieldWithSchema,
  openModal,
  validateAddress,
} from '../actions';
import {
  selectVAPContactInfoField,
  selectVAPServiceTransaction,
  selectCurrentlyOpenEditModal,
  selectEditedFormField,
} from '../selectors';
import VAPServiceProfileFieldHeading from '../components/base/VAPServiceProfileFieldHeading';
import VAPServiceTransaction from '../components/base/VAPServiceTransaction';
import ProfileInformationFieldController2 from '../components/ProfileInformationFieldController2';
import ContactInformationUpdateSuccessAlert from '../components/ContactInformationFieldInfo/ContactInformationUpdateSuccessAlert';

const CLOSE_MODAL_DELAY = 5000;

/**
 * Functional equivalent of the VAPServiceProfileField container. Used to
 * easily create components for VA Profile Service-backed contact information.
 *
 * Redux values arrive via useSelector/useDispatch rather than connect(), so
 * props are only the caller's contract (see propTypes). Intentional
 * deviations from the class are marked with "PORT NOTE" comments below:
 *   1. the close-modal timeout is cleared on unmount (the class leaked it)
 *   2. raw bound action creators are no longer forwarded to Content
 *   3. one refreshTransaction binding instead of two identical ones
 *
 * @property {string} fieldName The name of the property as it appears in the user.profile.vapContactInfo object.
 * @property {func} Content The component used to render the read-display of the field.
 * @property {string} title The field name converted to a visible display, such as for labels, modal titles, etc. Example: "mailingAddress" passes "Mailing address" as the title.
 * @property {string} apiRoute The API route used to create/update/delete the Vet360 field.
 * @property {func} [convertCleanDataToPayload] An optional function used to convert the clean edited data to a payload for sending to the API. Used to remove any values (especially falsy) that may cause errors in Vet360.
 */
export const ProfileField = ({
  alertClosingDisabled,
  apiRoute,
  Content,
  convertCleanDataToPayload,
  fieldName,
  title,
  ...rest
}) => {
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const dispatch = useDispatch();

  const analyticsSectionName = VAP_SERVICE.ANALYTICS_FIELD_MAP[fieldName];
  const data = useSelector(state =>
    selectVAPContactInfoField(state, fieldName),
  );
  const field = useSelector(state => selectEditedFormField(state, fieldName));
  const isEditing = useSelector(
    state => selectCurrentlyOpenEditModal(state) === fieldName,
  );
  const showValidationModal = useSelector(
    state =>
      state.vapService.addressValidation.addressValidationType === fieldName &&
      selectCurrentlyOpenEditModal(state) === 'addressValidation',
  );
  // shallowEqual is required here: selectVAPServiceTransaction builds a fresh
  // object on every call, so without it every store update would re-render
  // this component. connect() did the same comparison on the merged props.
  const { transaction, transactionRequest } = useSelector(
    state => selectVAPServiceTransaction(state, fieldName),
    shallowEqual,
  );
  const isEmpty = !data;

  const prevRef = useRef({ transaction, showValidationModal, isEditing });
  const closeModalTimeoutRef = useRef(null);

  useEffect(() => {
    const prev = prevRef.current;
    const modalWasOpen =
      prev.transaction || prev.showValidationModal || prev.isEditing;
    const modalIsClosed = !transaction && !showValidationModal && !isEditing;

    if (modalWasOpen && modalIsClosed) {
      focusElement(`button#${fieldName}-edit-link`);
    }

    // PORT NOTE 1: the class called setTimeout without keeping the id, so the
    // timer survived unmount and could dispatch openModal() against a field
    // that was no longer on the page. The id is now kept in
    // closeModalTimeoutRef and cleared by the unmount effect below.
    if (!prev.transaction && transaction) {
      closeModalTimeoutRef.current = setTimeout(
        () => dispatch(openModal()),
        CLOSE_MODAL_DELAY,
      );
    }

    const justClosedModal =
      (prev.isEditing && !isEditing) ||
      (prev.showValidationModal && !showValidationModal);
    if (justClosedModal && transaction) {
      focusElement(`div#${fieldName}-transaction-status`);
    }

    prevRef.current = { transaction, showValidationModal, isEditing };
  }, [transaction, showValidationModal, isEditing, fieldName, dispatch]);

  // PORT NOTE 1 (continued): unmount cleanup for the close-modal timer above.
  useEffect(() => () => clearTimeout(closeModalTimeoutRef.current), []);

  const openEditModal = () => {
    dispatch(openModal(fieldName));
    setShowSuccessAlert(false);
  };

  const closeModal = () => dispatch(openModal(null));

  const onAdd = () => {
    openEditModal();
  };

  const onEdit = () => {
    openEditModal();
  };

  const onCancel = () => {
    closeModal();
  };

  const onChangeFormDataAndSchemas = (value, schema, uiSchema) => {
    dispatch(updateFormFieldWithSchema(fieldName, value, schema, uiSchema));
  };

  const onDelete = () => {
    let payload = data;
    if (convertCleanDataToPayload) {
      payload = convertCleanDataToPayload(payload, fieldName);
    }
    dispatch(
      createTransaction(
        apiRoute,
        'DELETE',
        fieldName,
        payload,
        analyticsSectionName,
      ),
    );
  };

  const onSubmit = () => {
    const isAddressField = fieldName.toLowerCase().includes('address');

    let payload = field.value;
    if (convertCleanDataToPayload) {
      payload = convertCleanDataToPayload(payload, fieldName);
    }

    const method = payload.id ? 'PUT' : 'POST';

    if (isAddressField) {
      dispatch(
        validateAddress(
          apiRoute,
          method,
          fieldName,
          payload,
          analyticsSectionName,
        ),
      );
      return;
    }

    dispatch(
      createTransaction(
        apiRoute,
        method,
        fieldName,
        payload,
        analyticsSectionName,
      ),
    );
  };

  const clearErrors = () => dispatch(clearTransactionRequest(fieldName));

  // the class built two identical bindings of this call — one
  // for childProps and an inline arrow for VAPServiceTransaction's
  // refreshTransaction prop. Both now share this single handler.
  const handleRefreshTransaction = () =>
    dispatch(refreshTransaction(transaction, analyticsSectionName));

  const onSuccessfulSave = () => setShowSuccessAlert(true);

  const isEditLinkVisible = () => {
    const transactionPending = transaction
      ? isPendingTransaction(transaction)
      : false;
    return (
      !isEmpty && !(isEditing || showValidationModal) && !transactionPending
    );
  };

  // the class built childProps from `...this.props`, which
  // leaked the connect()-bound action creators (createTransaction, openModal,
  // etc.) into Content. Content components should use the curated handlers
  // below (onAdd, onEdit, onSubmit, clearErrors, ...) instead, and the only
  // Content in use (AddressView) reads just `data`, so the raw actions are
  // deliberately not forwarded. If a future Content needs one, add a
  // dispatch-wrapped version here rather than the raw action creator.
  const childProps = {
    ...rest,
    alertClosingDisabled,
    analyticsSectionName,
    apiRoute,
    Content,
    convertCleanDataToPayload,
    data,
    field,
    fieldName,
    isEditing,
    isEmpty,
    showValidationModal,
    title,
    transaction,
    transactionRequest,
    refreshTransaction: handleRefreshTransaction,
    clearErrors: alertClosingDisabled ? null : clearErrors,
    onAdd,
    onEdit,
    onChangeFormDataAndSchemas,
    onDelete,
    onCancel,
    onSubmit,
  };

  const shouldShowFields = isEditing || showValidationModal;

  return (
    <div className="vet360-profile-field" data-field-name={fieldName}>
      <VAPServiceProfileFieldHeading
        onEditClick={isEditLinkVisible() ? onEdit : null}
        fieldName={fieldName}
      >
        {title}
      </VAPServiceProfileFieldHeading>
      {showSuccessAlert && (
        <ContactInformationUpdateSuccessAlert fieldName={fieldName} />
      )}
      <div
        className={
          shouldShowFields ? 'vads-u-display--block' : 'vads-u-display--none'
        }
      >
        <ProfileInformationFieldController2
          fieldName={fieldName}
          isDeleteDisabled
          successCallback={onSuccessfulSave}
          cancelCallback={onCancel}
        />
      </div>
      <div
        className={
          !shouldShowFields ? 'vads-u-display--block' : 'vads-u-display--none'
        }
      >
        <VAPServiceTransaction
          isModalOpen={isEditing || showValidationModal}
          id={`${fieldName}-transaction-status`}
          title={title}
          transaction={transaction}
          transactionRequest={transactionRequest}
          refreshTransaction={handleRefreshTransaction}
        >
          {!isEmpty && <Content {...childProps} />}
        </VAPServiceTransaction>
      </div>
    </div>
  );
};

ProfileField.propTypes = {
  Content: PropTypes.func.isRequired,
  apiRoute: PropTypes.oneOf(Object.values(VAP_SERVICE.API_ROUTES)).isRequired,
  fieldName: PropTypes.oneOf(Object.values(VAP_SERVICE.FIELD_NAMES)).isRequired,
  title: PropTypes.string.isRequired,
  alertClosingDisabled: PropTypes.bool,
  convertCleanDataToPayload: PropTypes.func,
  deleteDisabled: PropTypes.bool,
};

export default ProfileField;
